if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      this.leftRail = Boolean(this.closest('.product--thumbnail_slider_left'));
      this.hoveredMediaId = null;
      this.committedMediaId = null;
      this.railScrolling = false;
      this.railScrollTimer = null;

      if (!this.elements.viewer || !this.elements.thumbnails) return;

      this.thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails-"]');
      this.thumbnailItems = () => Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
        .filter((item) => !item.classList.contains('slick-cloned'));

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 300));
      this.elements.thumbnails.addEventListener('click', this.onThumbnailClick.bind(this));
      this.elements.thumbnails.addEventListener('keydown', this.onThumbnailKeydown.bind(this));
      this.elements.thumbnails.addEventListener('pointerover', this.onThumbnailPointerOver.bind(this));
      this.elements.thumbnails.addEventListener('pointerout', this.onThumbnailPointerOut.bind(this));
      if (this.leftRail && this.thumbnailList) this.thumbnailList.addEventListener('scroll', this.onThumbnailScroll.bind(this), { passive: true });

      const activeMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
      this.committedMediaId = activeMedia ? activeMedia.dataset.mediaId : null;
      if (this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    findThumbnail(mediaId) {
      return this.thumbnailItems().find((item) => item.dataset.target === mediaId);
    }

    onSlideChanged(event) {
      const currentElement = event.detail && event.detail.currentElement;
      if (!currentElement) return;
      this.setActiveThumbnail(this.findThumbnail(currentElement.dataset.mediaId));
      this.committedMediaId = currentElement.dataset.mediaId;
    }

    onThumbnailClick(event) {
      const button = event.target.closest('button.thumbnail');
      if (!button || !this.elements.thumbnails.contains(button)) return;
      const item = button.closest('[data-target]');
      if (!item || item.classList.contains('slick-cloned')) return;
      event.preventDefault();
      this.setActiveMedia(item.dataset.target, false, { commit: true });
    }

    onThumbnailKeydown(event) {
      const button = event.target.closest('button.thumbnail');
      const item = button && button.closest('[data-target]');
      const items = this.thumbnailItems();
      const index = item && items.indexOf(item);
      if (!button || index < 0) return;

      const vertical = this.leftRail && this.mql.matches;
      let nextIndex = null;
      if ((vertical && event.key === 'ArrowDown') || (!vertical && event.key === 'ArrowRight')) nextIndex = index + 1;
      if ((vertical && event.key === 'ArrowUp') || (!vertical && event.key === 'ArrowLeft')) nextIndex = index - 1;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = items.length - 1;
      if (event.key === 'PageDown') nextIndex = Math.min(items.length - 1, index + this.visibleThumbnailCount());
      if (event.key === 'PageUp') nextIndex = Math.max(0, index - this.visibleThumbnailCount());
      if (nextIndex === null) return;

      event.preventDefault();
      const nextItem = items[nextIndex];
      const nextButton = nextItem && nextItem.querySelector('button.thumbnail');
      if (!nextButton) return;
      nextButton.focus();
      this.setActiveMedia(nextItem.dataset.target, false, { commit: true });
    }

    visibleThumbnailCount() {
      const items = this.thumbnailItems();
      if (!this.thumbnailList || !items.length) return 1;
      const first = items[0];
      const size = this.leftRail && this.mql.matches ? first.offsetHeight : first.offsetWidth;
      const viewport = this.leftRail && this.mql.matches ? this.thumbnailList.clientHeight : this.thumbnailList.clientWidth;
      return Math.max(1, Math.floor(viewport / Math.max(size, 1)));
    }

    onThumbnailPointerOver(event) {
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (this.railScrolling) return;
      const item = event.target.closest('[data-target]');
      if (!item || !this.elements.thumbnails.contains(item) || item.classList.contains('slick-cloned')) return;
      if (event.relatedTarget && item.contains(event.relatedTarget)) return;
      this.hoveredMediaId = item.dataset.target;
      this.setActiveMedia(this.hoveredMediaId, false, { commit: false, announce: false, scrollRail: false });
    }

    onThumbnailPointerOut(event) {
      const item = event.target.closest('[data-target]');
      if (!item || (event.relatedTarget && item.contains(event.relatedTarget))) return;
      if (this.railScrolling) return;
      if (this.hoveredMediaId !== item.dataset.target) return;
      this.hoveredMediaId = null;
      if (this.committedMediaId) this.setActiveMedia(this.committedMediaId, false, { commit: false, announce: false });
    }

    onThumbnailScroll() {
      if (!this.railScrolling && this.hoveredMediaId && this.committedMediaId) {
        const committedMediaId = this.committedMediaId;
        this.hoveredMediaId = null;
        this.setActiveMedia(committedMediaId, false, { commit: false, announce: false, scrollRail: false });
      }
      this.railScrolling = true;
      clearTimeout(this.railScrollTimer);
      this.railScrollTimer = setTimeout(() => {
        this.railScrolling = false;
      }, 180);
    }

    setActiveMedia(mediaId, prepend, options = {}) {
      const activeMedia = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'))
        .find((element) => element.dataset.mediaId === mediaId);
      if (!activeMedia) return;
      const commit = options.commit !== false;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => element.classList.remove('is-active'));
      activeMedia.classList.add('is-active');
      if (commit) {
        this.committedMediaId = mediaId;
        this.hoveredMediaId = null;
      }

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        const activeThumbnail = this.findThumbnail(mediaId);
        if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.viewer.slider && !(this.leftRail && this.mql.matches)) activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') activeMedia.scrollIntoView({ behavior: 'smooth' });
      });
      this.playActiveMedia(activeMedia);

      const activeThumbnail = this.findThumbnail(mediaId);
      this.setActiveThumbnail(activeThumbnail, options.scrollRail !== false);
      if (options.announce !== false && activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail, scrollRail = true) {
      if (!thumbnail) return;
      this.elements.thumbnails.querySelectorAll('button.thumbnail').forEach((element) => element.removeAttribute('aria-current'));
      const button = thumbnail.querySelector('button.thumbnail');
      if (!button) return;
      button.setAttribute('aria-current', 'true');
      if (!scrollRail) return;
      if (this.thumbnailList && this.thumbnailList.classList.contains('slick-initialized') && window.jQuery) {
        const index = this.thumbnailItems().indexOf(thumbnail);
        if (index >= 0) window.jQuery(this.thumbnailList).slick('slickGoTo', index, true);
      } else {
        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image || !this.elements.liveRegion) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (this.stickyHeader) this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
    }
  });
}
